import * as THREE from "three";

export type AxisIndex = 0 | 1 | 2;

export const POSITION_GIZMO_ROOT_NAME = "__positionGizmoRoot";

const AXIS_COLORS: [number, number, number] = [0xff3f5e, 0x3bd16f, 0x3f8bff];
// Visual size at camera distance == 1 world-unit. Handles are rescaled per frame
// based on camera distance so the gizmo feels constant on screen.
const BASE_SHAFT_LENGTH = 0.14;
const BASE_SHAFT_RADIUS = 0.0019;
const BASE_HEAD_LENGTH = 0.038;
const BASE_HEAD_RADIUS = 0.0135;
const BASE_CENTER_RADIUS = 0.0034;
const BASE_PICK_RADIUS = 0.027;
const HOVER_HEAD_SCALE = 1.38;
const HOVER_SHAFT_LENGTH_SCALE = 1.2;
const INACTIVE_OPACITY = 0.3;
const ACTIVE_OPACITY = 1.0;
const HOVER_ANIMATION_LERP_ALPHA = 0.42;
/** Drag-only: reverse-axis shaft grows from the origin over this duration (ms). */
const DRAG_REVERSE_REVEAL_MS = 220;

type AxisHandle = {
  axis: AxisIndex;
  pivot: THREE.Group; // rotates the handle to point along +axis
  shaft: THREE.Mesh;
  reverseShaft: THREE.Mesh;
  head: THREE.Mesh;
  reverseHead: THREE.Mesh;
  pick: THREE.Mesh;
  shaftMat: THREE.MeshBasicMaterial;
  reverseShaftMat: THREE.MeshBasicMaterial;
  headMat: THREE.MeshBasicMaterial;
  reverseHeadMat: THREE.MeshBasicMaterial;
  targetShaftLengthScale: number;
  currentShaftLengthScale: number;
  targetHeadScale: number;
  currentHeadScale: number;
  targetOpacity: number;
  currentOpacity: number;
};

export type PositionGizmo = {
  root: THREE.Group;
  handles: AxisHandle[];
  pickTargets: THREE.Object3D[];
  hoveredAxis: AxisIndex | null;
  draggingAxis: AxisIndex | null;
  /** `performance.now()` when current drag began; used to grow the reverse-axis segment. */
  dragReverseRevealStartMs: number | null;
};

const makeShaftPivotRotation = (axis: AxisIndex): THREE.Euler => {
  // Default cylinder/cone axis is +Y. Rotate pivot so the handle points along +X, +Y, or +Z.
  if (axis === 0) return new THREE.Euler(0, 0, -Math.PI / 2); // +Y → +X
  if (axis === 2) return new THREE.Euler(Math.PI / 2, 0, 0); // +Y → +Z
  return new THREE.Euler(0, 0, 0); // +Y stays +Y
};

const makeOverlayMaterial = (color: number): THREE.MeshBasicMaterial => {
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: ACTIVE_OPACITY,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  return mat;
};

const makeArrowHeadGeometry = (): THREE.LatheGeometry => {
  // Concave profile (dart-like) for a more polished arrow head than a plain cone.
  // Profile is centered around y=0 so positioning remains similar to ConeGeometry.
  const h = BASE_HEAD_LENGTH;
  const r = BASE_HEAD_RADIUS;
  const points = [
    new THREE.Vector2(0, -h / 2),
    new THREE.Vector2(r * 0.96, -h / 2),
    new THREE.Vector2(r * 0.92, -h * 0.32),
    new THREE.Vector2(r * 0.74, -h * 0.14),
    new THREE.Vector2(r * 0.5, h * 0.02),
    new THREE.Vector2(r * 0.32, h * 0.18),
    new THREE.Vector2(r * 0.17, h * 0.32),
    new THREE.Vector2(r * 0.07, h * 0.44),
    new THREE.Vector2(0, h / 2),
  ];
  return new THREE.LatheGeometry(points, 36);
};

export function createPositionGizmo(): PositionGizmo {
  const root = new THREE.Group();
  root.name = POSITION_GIZMO_ROOT_NAME;
  root.visible = false;
  root.renderOrder = 9999;
  root.userData.isPositionGizmo = true;

  const handles: AxisHandle[] = [];
  const pickTargets: THREE.Object3D[] = [];

  const centerMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const center = new THREE.Mesh(new THREE.SphereGeometry(BASE_CENTER_RADIUS, 14, 10), centerMat);
  center.renderOrder = 9999;
  center.userData.isPositionGizmo = true;
  center.userData.positionGizmoRole = "center";
  root.add(center);

  for (let axis = 0; axis < 3; axis++) {
    const color = AXIS_COLORS[axis];
    const pivot = new THREE.Group();
    pivot.name = `__positionGizmoAxis_${axis}`;
    pivot.rotation.copy(makeShaftPivotRotation(axis as AxisIndex));
    pivot.userData.isPositionGizmo = true;
    pivot.userData.positionGizmoAxis = axis;

    const shaftGeom = new THREE.CylinderGeometry(
      BASE_SHAFT_RADIUS,
      BASE_SHAFT_RADIUS,
      BASE_SHAFT_LENGTH,
      12
    );
    const shaftMat = makeOverlayMaterial(color);
    const shaft = new THREE.Mesh(shaftGeom, shaftMat);
    shaft.position.y = BASE_SHAFT_LENGTH / 2;
    shaft.renderOrder = 9999;
    shaft.userData.isPositionGizmo = true;
    shaft.userData.positionGizmoAxis = axis;
    shaft.userData.positionGizmoRole = "shaft";

    const reverseShaftMat = makeOverlayMaterial(color);
    const reverseShaft = new THREE.Mesh(shaftGeom.clone(), reverseShaftMat);
    reverseShaft.position.y = -BASE_SHAFT_LENGTH / 2;
    reverseShaft.renderOrder = 9999;
    // Y-scale animates from ~0 while dragging; tiny bounds can be frustum-culled otherwise.
    reverseShaft.frustumCulled = false;
    reverseShaft.visible = false;
    reverseShaft.userData.isPositionGizmo = true;
    reverseShaft.userData.positionGizmoAxis = axis;
    reverseShaft.userData.positionGizmoRole = "reverseShaft";

    const headGeom = makeArrowHeadGeometry();
    const headMat = makeOverlayMaterial(color);
    const head = new THREE.Mesh(headGeom, headMat);
    head.position.y = BASE_SHAFT_LENGTH + BASE_HEAD_LENGTH / 2;
    head.renderOrder = 9999;
    head.userData.isPositionGizmo = true;
    head.userData.positionGizmoAxis = axis;
    head.userData.positionGizmoRole = "head";

    const reverseHeadMat = makeOverlayMaterial(color);
    const reverseHead = new THREE.Mesh(headGeom.clone(), reverseHeadMat);
    reverseHead.position.y = -BASE_HEAD_LENGTH / 2;
    reverseHead.rotation.x = Math.PI;
    reverseHead.renderOrder = 9999;
    reverseHead.frustumCulled = false;
    reverseHead.visible = false;
    reverseHead.userData.isPositionGizmo = true;
    reverseHead.userData.positionGizmoAxis = axis;
    reverseHead.userData.positionGizmoRole = "reverseHead";

    // Invisible fat cylinder covering shaft+head for easier picking.
    const pickLength = BASE_SHAFT_LENGTH + BASE_HEAD_LENGTH;
    const pickGeom = new THREE.CylinderGeometry(BASE_PICK_RADIUS, BASE_PICK_RADIUS, pickLength, 12);
    const pickMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      visible: true,
    });
    const pick = new THREE.Mesh(pickGeom, pickMat);
    pick.position.y = pickLength / 2;
    pick.renderOrder = 9999;
    pick.userData.isPositionGizmo = true;
    pick.userData.positionGizmoAxis = axis;
    pick.userData.positionGizmoRole = "pick";

    pivot.add(pick);
    pivot.add(shaft);
    pivot.add(reverseShaft);
    pivot.add(head);
    pivot.add(reverseHead);
    root.add(pivot);

    handles.push({
      axis: axis as AxisIndex,
      pivot,
      shaft,
      reverseShaft,
      head,
      reverseHead,
      pick,
      shaftMat,
      reverseShaftMat,
      headMat,
      reverseHeadMat,
      targetShaftLengthScale: 1,
      currentShaftLengthScale: 1,
      targetHeadScale: 1,
      currentHeadScale: 1,
      targetOpacity: ACTIVE_OPACITY,
      currentOpacity: ACTIVE_OPACITY,
    });
    pickTargets.push(pick);
  }

  return {
    root,
    handles,
    pickTargets,
    hoveredAxis: null,
    draggingAxis: null,
    dragReverseRevealStartMs: null,
  };
}

export function disposePositionGizmo(gizmo: PositionGizmo): void {
  if (!gizmo || !gizmo.root) return;
  try {
    gizmo.root.parent?.remove(gizmo.root);
  } catch {
    // ignore
  }
  gizmo.root.traverse((obj) => {
    const mesh = obj as THREE.Object3D & {
      geometry?: THREE.BufferGeometry;
      material?: THREE.Material | THREE.Material[];
    };
    if (mesh.geometry && typeof mesh.geometry.dispose === "function") {
      try {
        mesh.geometry.dispose();
      } catch {
        // ignore
      }
    }
    const mat = mesh.material;
    if (!mat) return;
    if (Array.isArray(mat)) {
      mat.forEach((m) => {
        try {
          m?.dispose?.();
        } catch {
          // ignore
        }
      });
    } else {
      try {
        mat.dispose?.();
      } catch {
        // ignore
      }
    }
  });
}

function applyAxisVisualTargets(gizmo: PositionGizmo): void {
  const hovered = gizmo.hoveredAxis;
  const dragging = gizmo.draggingAxis;
  for (const handle of gizmo.handles) {
    const isDraggingAxis = dragging != null && dragging === handle.axis;
    const isHoveredOnly = dragging == null && hovered === handle.axis;
    handle.targetHeadScale = isHoveredOnly || isDraggingAxis ? HOVER_HEAD_SCALE : 1;
    handle.targetShaftLengthScale = isHoveredOnly || isDraggingAxis ? HOVER_SHAFT_LENGTH_SCALE : 1;
    handle.shaft.scale.set(1, 1, 1);
    handle.reverseShaft.scale.set(1, 1, 1);
    if (dragging != null) {
      handle.targetOpacity = isDraggingAxis ? ACTIVE_OPACITY : 0;
      handle.reverseHead.visible = isDraggingAxis;
      handle.reverseShaft.visible = isDraggingAxis;
    } else {
      handle.targetOpacity =
        hovered == null ? ACTIVE_OPACITY : isHoveredOnly ? ACTIVE_OPACITY : INACTIVE_OPACITY;
      handle.reverseHead.visible = false;
      handle.reverseShaft.visible = false;
    }
  }
}

export function setHoveredAxis(gizmo: PositionGizmo, hovered: AxisIndex | null): void {
  gizmo.hoveredAxis = hovered;
  applyAxisVisualTargets(gizmo);
}

export function setDraggingAxis(gizmo: PositionGizmo, dragging: AxisIndex | null): void {
  gizmo.draggingAxis = dragging;
  if (dragging != null) {
    gizmo.hoveredAxis = dragging;
    gizmo.dragReverseRevealStartMs = typeof performance !== "undefined" ? performance.now() : null;
  } else {
    gizmo.dragReverseRevealStartMs = null;
  }
  applyAxisVisualTargets(gizmo);
}

/**
 * Position gizmo at `origin` (scene-world), orient its +X/+Y/+Z to the given world
 * rotation (parent-local frame expressed in world), and rescale so it feels
 * constant on screen at `cameraDistance`.
 */
export function updateGizmoTransform(
  gizmo: PositionGizmo,
  origin: THREE.Vector3,
  parentWorldQuat: THREE.Quaternion,
  cameraDistance: number
): void {
  const now = typeof performance !== "undefined" ? performance.now() : 0;
  let dragReverseGrow = 1;
  if (gizmo.draggingAxis != null && gizmo.dragReverseRevealStartMs != null) {
    const u = THREE.MathUtils.clamp(
      (now - gizmo.dragReverseRevealStartMs) / DRAG_REVERSE_REVEAL_MS,
      0,
      1
    );
    // smootherstep(x, min, max) — x is the progress value, not the first edge.
    dragReverseGrow = THREE.MathUtils.smootherstep(u, 0, 1);
  }

  for (const handle of gizmo.handles) {
    handle.currentShaftLengthScale = THREE.MathUtils.lerp(
      handle.currentShaftLengthScale,
      handle.targetShaftLengthScale,
      HOVER_ANIMATION_LERP_ALPHA
    );
    if (Math.abs(handle.currentShaftLengthScale - handle.targetShaftLengthScale) < 0.0015) {
      handle.currentShaftLengthScale = handle.targetShaftLengthScale;
    }
    handle.shaft.scale.set(1, handle.currentShaftLengthScale, 1);
    handle.shaft.position.y = (BASE_SHAFT_LENGTH * handle.currentShaftLengthScale) / 2;

    const isDraggingThisAxis = gizmo.draggingAxis != null && gizmo.draggingAxis === handle.axis;

    if (isDraggingThisAxis) {
      const g = dragReverseGrow;
      const shaftLen = BASE_SHAFT_LENGTH * handle.currentShaftLengthScale;
      handle.reverseShaft.scale.set(1, handle.currentShaftLengthScale * g, 1);
      handle.reverseShaft.position.y = -(shaftLen * g) / 2;
    } else {
      handle.reverseShaft.scale.set(1, handle.currentShaftLengthScale, 1);
      handle.reverseShaft.position.y = -(BASE_SHAFT_LENGTH * handle.currentShaftLengthScale) / 2;
    }

    handle.currentHeadScale = THREE.MathUtils.lerp(
      handle.currentHeadScale,
      handle.targetHeadScale,
      HOVER_ANIMATION_LERP_ALPHA
    );
    if (Math.abs(handle.currentHeadScale - handle.targetHeadScale) < 0.0015) {
      handle.currentHeadScale = handle.targetHeadScale;
    }
    handle.head.scale.set(
      handle.currentHeadScale,
      handle.currentHeadScale,
      handle.currentHeadScale
    );
    handle.head.position.y =
      BASE_SHAFT_LENGTH * handle.currentShaftLengthScale + BASE_HEAD_LENGTH / 2;

    if (isDraggingThisAxis) {
      const g = dragReverseGrow;
      const shaftLen = BASE_SHAFT_LENGTH * handle.currentShaftLengthScale;
      const headPop = THREE.MathUtils.smootherstep(g, 0.78, 1);
      handle.reverseHead.position.y = -(shaftLen * g + BASE_HEAD_LENGTH / 2);
      const hs = handle.currentHeadScale * headPop;
      handle.reverseHead.scale.set(hs, hs, hs);
    } else {
      handle.reverseHead.scale.set(
        handle.currentHeadScale,
        handle.currentHeadScale,
        handle.currentHeadScale
      );
      handle.reverseHead.position.y = -(
        BASE_SHAFT_LENGTH * handle.currentShaftLengthScale +
        BASE_HEAD_LENGTH / 2
      );
    }

    handle.currentOpacity = THREE.MathUtils.lerp(
      handle.currentOpacity,
      handle.targetOpacity,
      HOVER_ANIMATION_LERP_ALPHA
    );
    if (Math.abs(handle.currentOpacity - handle.targetOpacity) < 0.0015) {
      handle.currentOpacity = handle.targetOpacity;
    }
    handle.shaftMat.opacity = handle.currentOpacity;
    handle.reverseShaftMat.opacity = handle.currentOpacity;
    handle.headMat.opacity = handle.currentOpacity;
    handle.reverseHeadMat.opacity = handle.currentOpacity;
  }

  gizmo.root.position.copy(origin);
  gizmo.root.quaternion.copy(parentWorldQuat);
  const s = Math.max(0.05, cameraDistance) * 0.64;
  gizmo.root.scale.setScalar(s);
  gizmo.root.updateMatrixWorld(true);
}

/** World-space direction of an axis handle after applying the gizmo's orientation. */
export function getAxisWorldDirection(
  gizmo: PositionGizmo,
  axis: AxisIndex,
  out: THREE.Vector3
): THREE.Vector3 {
  const basis = new THREE.Vector3();
  if (axis === 0) basis.set(1, 0, 0);
  else if (axis === 1) basis.set(0, 1, 0);
  else basis.set(0, 0, 1);
  basis.applyQuaternion(gizmo.root.quaternion);
  return out.copy(basis).normalize();
}
